import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ChartBarIcon,
  ChevronDownIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  TruckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import vehicleMaintenanceApi from '../../api/vehicleMaintenanceApi';
import { sortRowsByVehicleCode, sortVehiclesByVehicleCode } from './internalAuditVehicleCodeSort';
import {
  VehicleModuleHero,
  VehiclePageShell,
  VehiclePlateCell,
  vehiclePlateColumnProps,
  VehicleSelectionPreview,
  VehicleStatCard,
  VehicleStatGrid,
  getVehicleById,
} from './vehicleMaintenanceUi';
import ActionToolbar from '../../components/ui/ActionToolbar';
import DataTable from '../../components/ui/DataTable';
import FilterActions from '../../components/ui/FilterActions';
import FilterPanel from '../../components/ui/FilterPanel';
import IconActionButton from '../../components/ui/IconActionButton';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import ResponsiveModal from '../../components/ui/ResponsiveModal';

const initialForm = {
  vehicle: '',
  week_start_date: '',
  week_end_date: '',
  kilometers: '',
  notes: '',
};

const InternalAuditMileagePage = () => {
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [filters, setFilters] = useState({ vehicle_id: '' });
  const [draftFilters, setDraftFilters] = useState({ vehicle_id: '' });
  const vehicleDropdownRef = useRef(null);

  const sortedVehicles = useMemo(() => sortVehiclesByVehicleCode(vehicles), [vehicles]);

  const sortedRows = useMemo(() => sortRowsByVehicleCode(rows), [rows]);

  const loadData = async (activeFilters = filters) => {
    setLoading(true);
    setError(null);
    try {
      const [mileageResponse, vehiclesResponse] = await Promise.all([
        vehicleMaintenanceApi.getMileageLogs(activeFilters),
        vehicleMaintenanceApi.getVehicles({ is_active: 'true' }),
      ]);
      setRows(mileageResponse?.data || []);
      setVehicles(vehiclesResponse?.data || []);
    } catch (err) {
      setError(err.message || 'KM kayıtları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!vehicleDropdownRef.current) return;
      if (!vehicleDropdownRef.current.contains(event.target)) setShowVehicleDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = () => {
    setFilters(draftFilters);
    setShowVehicleDropdown(false);
    loadData(draftFilters);
  };

  const handleClear = () => {
    const reset = { vehicle_id: '' };
    setDraftFilters(reset);
    setFilters(reset);
    setShowVehicleDropdown(false);
    loadData(reset);
  };

  const escapeCsv = (val) => {
    const s = val == null ? '' : String(val);
    if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };

  const handleExportCsv = () => {
    const header = ['vehicle_code', 'license_plate', 'week_start_date', 'week_end_date', 'kilometers', 'notes'];
    const lines = [header.join(',')];
    sortedRows.forEach((row) => {
      lines.push([
        escapeCsv(row.vehicle_code),
        escapeCsv(row.license_plate),
        escapeCsv(row.week_start_date),
        escapeCsv(row.week_end_date),
        escapeCsv(row.kilometers),
        escapeCsv(row.notes),
      ].join(','));
    });
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `internal-audit-mileage-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openCreateModal = () => {
    setEditingRow(null);
    setSelectedRow(null);
    setFormData(initialForm);
    setIsModalOpen(true);
  };

  const openEditModal = (row) => {
    if (!row) return;
    setEditingRow(row);
    setFormData({
      vehicle: row.vehicle || '',
      week_start_date: row.week_start_date || '',
      week_end_date: row.week_end_date || '',
      kilometers: row.kilometers || '',
      notes: row.notes || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (row) => {
    if (!row) return;
    if (!window.confirm('KM kaydını silmek istediğinize emin misiniz?')) return;
    try {
      await vehicleMaintenanceApi.deleteMileageLog(row.id);
      setSelectedRow(null);
      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt silinemedi.');
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...formData,
        kilometers: formData.kilometers ? Number(formData.kilometers) : null,
      };
      if (editingRow?.id) {
        await vehicleMaintenanceApi.updateMileageLog(editingRow.id, payload);
      } else {
        await vehicleMaintenanceApi.createMileageLog(payload);
      }
      setIsModalOpen(false);
      setEditingRow(null);
      setSelectedRow(null);
      setFormData(initialForm);
      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => [
    {
      header: 'Araç ID',
      accessorKey: 'vehicle_code',
      cell: ({ getValue }) => (
        <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
          {getValue() || '—'}
        </span>
      ),
    },
    {
      ...vehiclePlateColumnProps,
      cell: ({ row }) => {
        const vehicle = getVehicleById(vehicles, row.original.vehicle);
        return (
          <VehiclePlateCell
            plate={row.original.license_plate}
            brand={vehicle?.brand}
            model={vehicle?.model}
          />
        );
      },
    },
    { header: 'Hafta Başlangıç', accessorKey: 'week_start_date', cell: ({ getValue }) => getValue() || '—' },
    { header: 'Hafta Bitiş', accessorKey: 'week_end_date', cell: ({ getValue }) => getValue() || '—' },
    {
      header: 'KM',
      accessorKey: 'kilometers',
      cell: ({ getValue }) => {
        const v = getValue();
        if (v === null || v === undefined || v === '') return '—';
        return Number(v).toLocaleString('tr-TR');
      },
    },
    { header: 'Notlar', accessorKey: 'notes', cell: ({ getValue }) => getValue() || '—' },
  ], [vehicles]);

  const stats = useMemo(() => {
    const total = sortedRows.length;
    const uniqueVehicles = new Set(sortedRows.map((r) => r.vehicle).filter(Boolean)).size;
    const totalKm = sortedRows.reduce((sum, r) => sum + (Number(r.kilometers) || 0), 0);
    const avgKm = total > 0 ? Math.round(totalKm / total) : 0;
    return {
      total,
      uniqueVehicles,
      totalKm: totalKm.toLocaleString('tr-TR'),
      avgKm: avgKm.toLocaleString('tr-TR'),
    };
  }, [sortedRows]);

  const selectedVehiclePreview = useMemo(() => {
    if (!selectedRow) return null;
    return getVehicleById(vehicles, selectedRow.vehicle) || {
      license_plate: selectedRow.license_plate,
      vehicle_code: selectedRow.vehicle_code,
      brand: null,
      model: null,
      assigned_person: null,
    };
  }, [selectedRow, vehicles]);

  return (
    <VehiclePageShell>
        <VehicleModuleHero
          icon={ChartBarIcon}
          title="KM Takip"
          subtitle="Haftalık kilometre girişlerini araç bazlı yönetin."
          accent="teal"
        />

        <VehicleStatGrid>
          <VehicleStatCard label="Toplam Kayıt" value={stats.total} tone="slate" icon={ChartBarIcon} />
          <VehicleStatCard label="Araç Sayısı" value={stats.uniqueVehicles} tone="blue" icon={TruckIcon} />
          <VehicleStatCard label="Toplam KM" value={stats.totalKm} tone="emerald" icon={ChartBarIcon} />
          <VehicleStatCard label="Ortalama KM" value={stats.avgKm} tone="amber" icon={ChartBarIcon} />
        </VehicleStatGrid>

        {selectedVehiclePreview ? (
          <VehicleSelectionPreview vehicle={selectedVehiclePreview} personName={selectedVehiclePreview.assigned_person} />
        ) : null}

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            <div className="flex items-center justify-between gap-3">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : null}

        <FilterPanel compact title="Filtreler" isOpen={isFilterOpen} onToggle={() => setIsFilterOpen((prev) => !prev)}>
          <div className="grid grid-cols-1 gap-1.5">
            <div className="rounded-md border border-teal-100/80 bg-gradient-to-br from-white to-teal-50/40 p-1.5 dark:border-teal-900/40 dark:from-gray-800 dark:to-teal-950/20">
              <div className="sr-only text-teal-800 dark:text-teal-300">
                Araç Filtresi
              </div>
              <div className="grid grid-cols-2 gap-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7">
                <fieldset className="rounded border border-gray-200 bg-white px-1 pb-0 pt-0 dark:border-gray-600 dark:bg-gray-700">
                  <legend className="px-0.5 text-[8px] text-gray-500 dark:text-gray-400 leading-none">Araç</legend>
                  <div className="relative" ref={vehicleDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setShowVehicleDropdown((prev) => !prev)}
                      className="flex h-5 w-full items-center justify-between bg-transparent px-0 text-[10px] text-gray-900 outline-none dark:text-white"
                    >
                      <span className="truncate">
                        {(() => {
                          if (!draftFilters.vehicle_id) return 'Tümü';
                          const selected = sortedVehicles.find((v) => String(v.id) === String(draftFilters.vehicle_id));
                          return selected ? `${selected.vehicle_code} - ${selected.license_plate}` : 'Tümü';
                        })()}
                      </span>
                      <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
                    </button>
                    {showVehicleDropdown ? (
                      <div className="absolute left-0 right-0 z-50 mt-1 max-h-[196px] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
                        <button
                          type="button"
                          onClick={() => {
                            setDraftFilters((prev) => ({ ...prev, vehicle_id: '' }));
                            setShowVehicleDropdown(false);
                          }}
                          className="w-full px-2 py-1.5 text-left text-[11px] text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                        >
                          Tümü
                        </button>
                        {sortedVehicles.map((vehicle) => (
                          <button
                            key={vehicle.id}
                            type="button"
                            onClick={() => {
                              setDraftFilters((prev) => ({ ...prev, vehicle_id: String(vehicle.id) }));
                              setShowVehicleDropdown(false);
                            }}
                            className="w-full px-2 py-1.5 text-left text-[11px] text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                          >
                            {vehicle.vehicle_code} - {vehicle.license_plate}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </fieldset>
              </div>
            </div>
          </div>
          <div className="mt-1.5 flex justify-end border-t border-gray-100 pt-1.5 dark:border-gray-700">
            <FilterActions compact onClear={handleClear} onSearch={handleSearch} />
          </div>
        </FilterPanel>

        <ActionToolbar className="rounded-xl border border-teal-100/80 bg-white/80 px-2 py-1.5 shadow-sm dark:border-teal-900/40 dark:bg-gray-900/60">
          <IconActionButton icon={PlusIcon} onClick={openCreateModal} title="Yeni Kayıt" />
          <IconActionButton icon={PencilSquareIcon} onClick={() => openEditModal(selectedRow)} title="Düzenle" disabled={!selectedRow} />
          <IconActionButton icon={TrashIcon} onClick={() => handleDelete(selectedRow)} title="Sil" disabled={!selectedRow} />
          <IconActionButton icon={ArrowDownTrayIcon} onClick={handleExportCsv} title="CSV dışa aktar" />
        </ActionToolbar>

        <DataTable
          data={sortedRows}
          columns={columns}
          isLoading={loading}
          showSearch={false}
          onRowClick={setSelectedRow}
          onRowDoubleClick={openEditModal}
          selectedRowId={selectedRow?.id}
          compact
          compactCellWrap
          modern
        />

      <ResponsiveModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="max-w-3xl">
        <div className="px-4 pt-5 pb-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              {editingRow ? 'KM Kaydını Düzenle' : 'Yeni KM Kaydı'}
            </h2>
            <button type="button" onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSave}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Araç</Label>
                <select required className="flex h-11 md:h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white" value={formData.vehicle} onChange={(e) => setFormData((prev) => ({ ...prev, vehicle: e.target.value }))}>
                  <option value="">Araç seçin</option>
                  {sortedVehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.vehicle_code} - {vehicle.license_plate}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Kilometre</Label>
                <Input required type="number" value={formData.kilometers} onChange={(e) => setFormData((prev) => ({ ...prev, kilometers: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Hafta Başlangıç</Label>
                <Input required type="date" value={formData.week_start_date} onChange={(e) => setFormData((prev) => ({ ...prev, week_start_date: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Hafta Bitiş</Label>
                <Input type="date" value={formData.week_end_date} onChange={(e) => setFormData((prev) => ({ ...prev, week_end_date: e.target.value }))} />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Notlar</Label>
                <Input value={formData.notes} onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))} />
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end space-x-3">
              <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)}>İptal</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Button>
            </div>
          </form>
        </div>
      </ResponsiveModal>
    </VehiclePageShell>
  );
};

export default InternalAuditMileagePage;
